/**
 * Eigenvector centrality is a measure of the influence a node has on a network.
 * If a node is pointed to by many nodes (which also have high eigenvector centrality)
 * then that node will have high eigenvector centrality.
 *
 * https://en.wikipedia.org/wiki/Eigenvector_centrality
 *
 * Over `count` nodes (0..count-1) with undirected neighbours given as CSR arrays; returns each node's score.
 */
export const eigenvectorCentrality = (
  count: number,
  offsets: Uint32Array,
  neighbours: Uint32Array,
  { maxIter = 100, tolerance = 1e-6 }: { maxIter?: number; tolerance?: number } = {},
): Float64Array => {
  let x = new Float64Array(count).fill(count > 0 ? 1 / count : 0);
  const totalTolerance = count * tolerance;
  for (let i = 0; i < maxIter; i++) {
    const xlast = x;
    x = new Float64Array(count);
    // do the multiplication
    for (let n = 0; n < count; n++) {
      for (let p = offsets[n]; p < offsets[n + 1]; p++) x[neighbours[p]] += xlast[n];
    }
    // Normalize the vector
    let sum = 0;
    for (let n = 0; n < count; n++) sum += x[n] * x[n];
    sum = Math.sqrt(sum);
    sum = sum === 0 ? 1 : 1 / sum;
    let error = 0;
    for (let n = 0; n < count; n++) {
      x[n] *= sum;
      // Check error convergence
      error += Math.abs(x[n] - xlast[n]);
    }
    if (error < totalTolerance) return x;
  }
  console.log("failed to converge");
  return x;
};
